import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { useScopedEmployeeSearch } from '../../hooks/use-leave-manager';
import { LeaveSheetView } from '../leave-sheet-view/leave-sheet-view';

const SEARCH_DEBOUNCE_MS = 300;

/** The employee whose Leave Sheet shows, kept in the URL so an Overview row can open it. */
export const EMPLOYEE_PARAM = 'employee';

/**
 * Employee dropdown + their Leave Sheet. The dropdown searches on the server within the Leave Manager's
 * scope (`useScopedEmployeeSearch`); the picked employee is the `employee` URL parameter, which an
 * Overview row sets to open that person's sheet (Component Map › Leave Sheet replaces the Leave Details
 * drawer).
 */
export const LeaveSheetTab = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const userId = params.get(EMPLOYEE_PARAM) ?? '';
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  // Only the employees the Leave Manager lists (admins: all; heads: their departments).
  const { data: employees = [], isLoading } = useScopedEmployeeSearch(keyword);
  const { byUserId } = useEmployeesByUserIds(userId ? [userId] : []);
  const selectedName =
    employees.find((option) => option.UserId === userId)?.DisplayName ??
    byUserId.get(userId)?.DisplayName;
  const options = employees.map((option) => ({
    value: option.UserId,
    label: option.DisplayName,
    description: [option.Designation, option.DepartmentName, option.Email]
      .filter(Boolean)
      .join(' · '),
  }));

  const pick = (next: string) =>
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next) updated.set(EMPLOYEE_PARAM, next);
        else updated.delete(EMPLOYEE_PARAM);
        return updated;
      },
      { replace: true }
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-md">
        <SearchableSelect
          value={userId}
          options={options}
          onChange={pick}
          onSearchChange={setInput}
          selectedLabel={selectedName}
          placeholder={t('SELECT_EMPLOYEE')}
          searchPlaceholder={t('SEARCH_EMPLOYEE')}
          emptyText={t('NO_DATA_FOUND')}
          isLoading={isLoading}
        />
      </div>

      {userId ? (
        <LeaveSheetView key={userId} userId={userId} canPickTerm />
      ) : (
        <p className="text-sm text-muted-foreground">{t('SELECT_EMPLOYEE_TO_VIEW_LEAVE_SHEET')}</p>
      )}
    </div>
  );
};
